import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useDarkMode } from '../../context/DarkModeContext';
import {
  HomeIcon,
  CalendarIcon,
  UserGroupIcon,
  UserIcon,
  CogIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  TicketIcon,
  SunIcon,
  MoonIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import '../../styles/components.css';
import { useConcessionTypes, poolDisplayName } from '../../utils/concessions';
import ConcessionTypesModal from '../admin/components/ConcessionTypesModal';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [purseDropdownOpen, setPurseDropdownOpen] = useState(false);
  const [showConcessionsModal, setShowConcessionsModal] = useState(false);
  const { user, logout, isAdmin, isImpersonating, endImpersonation } = useAuth();
  const { isDarkMode, toggleDarkMode, resetToSystemPreference, isFollowingSystem } = useDarkMode();
  const { types: concessionTypeNames } = useConcessionTypes(!isAdmin);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const purseDropdownRef = useRef(null);

  // Per-class-type concession purses this user is enrolled in.
  // Marked users see their marked purses (always, even at 0 or -2) instead of
  // the legacy shared badge; unmarked users keep today's shared-only badge.
  const markedPurses = (Array.isArray(user?.concessionTypes) ? user.concessionTypes : [])
    .map((key) => ({
      key,
      name: poolDisplayName(concessionTypeNames, key),
      balance: (user?.concessionBalances && typeof user.concessionBalances[key] === 'number')
        ? user.concessionBalances[key]
        : 0
    }));

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
      if (purseDropdownRef.current && !purseDropdownRef.current.contains(event.target)) {
        setPurseDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const mainNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    ...(isAdmin ? [] : [
      { name: 'Class Schedule', href: '/classes', icon: CalendarIcon }
    ]),
    ...(isAdmin ? [
      ...(user?.role === 'admin' ? [
        { name: 'Manage Users', href: '/admin/users', icon: UserGroupIcon },
        { name: 'Manage Classes', href: '/admin/classes', icon: CalendarIcon },
        { name: 'Surveys', href: '/admin/surveys', icon: ClipboardDocumentListIcon }
      ] : [
        { name: 'View Users', href: '/admin/users', icon: UserGroupIcon },
        { name: 'View Classes', href: '/admin/classes', icon: CalendarIcon },
        { name: 'Surveys', href: '/admin/surveys', icon: ClipboardDocumentListIcon }
      ])
    ] : [])
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <div className="min-h-screen bg-primary">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-card shadow-xl">
          <div className="flex h-16 items-center justify-between px-4 border-b border-primary">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <span className="ml-3 text-lg font-bold text-primary">FitFlow</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-secondary hover:text-primary"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          
          <nav className="flex-1 space-y-1 px-3 py-4">
            {mainNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                  isActive(item.href)
                    ? 'bg-active text-primary border-r-2 border-primary'
                    : 'text-secondary hover:bg-hover hover:text-primary'
                }`}
              >
                <item.icon
                  className={`mr-3 w-5 h-5 flex-shrink-0 transition-colors duration-200 ${
                    isActive(item.href) ? 'text-primary' : 'text-tertiary group-hover:text-primary'
                  }`}
                />
                {item.name}
              </Link>
            ))}
            {user?.role === 'admin' && (
              <button
                onClick={() => { setShowConcessionsModal(true); setSidebarOpen(false); }}
                className="group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 text-secondary hover:bg-hover hover:text-primary w-full text-left"
              >
                <TicketIcon className="mr-3 w-5 h-5 flex-shrink-0 text-tertiary group-hover:text-primary transition-colors duration-200" />
                Concessions
              </button>
            )}
          </nav>
        </div>
      </div>


      {/* Main content */}
      <div>
        {/* Top header */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-primary bg-card px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          {/* Mobile menu button */}
          <button
            type="button"
            className="-m-2.5 p-2.5 text-secondary lg:hidden hover:bg-hover rounded-lg transition-colors duration-200"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="w-6 h-6" />
          </button>

          {/* Brand/Logo */}
          <button 
            onClick={() => navigate(isAdmin ? '/admin/users' : '/dashboard')}
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center gap-2.5">
            {/* Dashboard Icon */}
            <Link
              to="/dashboard"
              className={`flex items-center px-4 py-2 rounded-lg transition-all duration-200 ${
                isActive('/dashboard')
                  ? 'bg-active text-primary'
                  : 'text-secondary hover:bg-hover hover:text-primary'
              }`}
            >
              <HomeIcon className="w-5 h-5 mr-2" />
              <span className="text-sm font-medium">Home</span>
            </Link>

            {/* Navigation Items */}
            {isAdmin ? (
              <>
                <Link
                  to="/admin/users"
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive('/admin/users')
                      ? 'bg-active text-primary'
                      : 'text-secondary hover:bg-hover hover:text-primary'
                  }`}
                >
                  {user?.role === 'admin' ? 'Manage Users' : 'View Users'}
                </Link>
                <Link
                  to="/admin/classes"
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive('/admin/classes')
                      ? 'bg-active text-primary'
                      : 'text-secondary hover:bg-hover hover:text-primary'
                  }`}
                >
                  {user?.role === 'admin' ? 'Manage Classes' : 'View Classes'}
                </Link>
                {user?.role === 'admin' && (
                  <button
                    onClick={() => setShowConcessionsModal(true)}
                    className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      showConcessionsModal
                        ? 'bg-active text-primary'
                        : 'text-secondary hover:bg-hover hover:text-primary'
                    }`}
                  >
                    Concessions
                  </button>
                )}
                <Link
                  to="/admin/surveys"
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive('/admin/surveys')
                      ? 'bg-active text-primary'
                      : 'text-secondary hover:bg-hover hover:text-primary'
                  }`}
                >
                  Surveys
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/classes"
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive('/classes')
                      ? 'bg-active text-primary'
                      : 'text-secondary hover:bg-hover hover:text-primary'
                  }`}
                >
                  Class Schedule
                </Link>
              </>
            )}
          </div>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1"></div>
            
            {/* User menu */}
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-secondary" />
              
              {/* Concessions Badge - Only show for regular users.
                  No marks: shared balance as today.
                  One mark: single plain badge for that purse (always shown).
                  Multiple marks: dropdown of all marked purses (always shown). */}
              {!isAdmin && (
                markedPurses.length === 0 ? (
                  <div className="flex items-center gap-x-2 px-3 py-2 bg-tertiary rounded-lg border border-primary">
                    <TicketIcon className="w-4 h-4 text-primary" />
                    <span
                      className="text-sm font-medium text-secondary hidden lg:inline"
                      id="concessions-text"
                    >
                      Concessions:
                    </span>
                    <span className="text-sm font-bold text-primary">{user?.concessions || 0}</span>
                  </div>
                ) : markedPurses.length === 1 ? (
                  <div
                    className="flex items-center gap-x-2 px-3 py-2 bg-tertiary rounded-lg border border-primary"
                    title={`${markedPurses[0].name} concessions`}
                  >
                    <TicketIcon className="w-4 h-4 text-primary" />
                    <span
                      className="text-sm font-medium text-secondary hidden lg:inline"
                      id="concessions-text"
                    >
                      {markedPurses[0].name}:
                    </span>
                    <span className={`text-sm font-bold ${markedPurses[0].balance < 0 ? 'text-red-500' : 'text-primary'}`}>
                      {markedPurses[0].balance}
                    </span>
                  </div>
                ) : (
                  <div className="relative" ref={purseDropdownRef}>
                    <button
                      onClick={() => setPurseDropdownOpen((open) => !open)}
                      className="flex items-center gap-x-2 px-3 py-2 bg-tertiary rounded-lg border border-primary"
                    >
                      <TicketIcon className="w-4 h-4 text-primary" />
                      <ChevronDownIcon className={`w-4 h-4 text-secondary transition-transform ${purseDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {purseDropdownOpen && (
                      <div
                        className="absolute right-0 mt-2 w-48 bg-card rounded-lg shadow-lg border border-primary z-50"
                        style={{ minWidth: '180px' }}
                      >
                        <div className="px-3 py-2 text-xs text-secondary border-b border-primary">
                          Concession Wallet
                        </div>
                        {markedPurses.map((p) => (
                          <div key={p.key} className="flex items-center justify-between px-3 py-2">
                            <span className="text-sm text-secondary">{p.name}</span>
                            <span className={`text-sm font-bold ${p.balance < 0 ? 'text-red-500' : 'text-primary'}`}>
                              {p.balance}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              )}
              
              <div className="relative flex items-center gap-x-2" ref={dropdownRef}>
                {/* Admin/Coach Badge - Show when admin or coach and not impersonating */}
                {(user?.role === 'admin' || user?.role === 'coach') && !isImpersonating && (
                  <span 
                    className="user-role-badge-inline"
                    style={{
                      backgroundColor: user?.role === 'admin' ? '#fecaca' : '#fef3c7',
                      color: user?.role === 'admin' ? '#991b1b' : '#92400e'
                    }}
                  >
                    {user?.role === 'admin' ? 'Admin' : 'Coach'}
                  </span>
                )}
                
                {/* Impersonation Pill - Only show when impersonating, positioned next to profile */}
                {isImpersonating && (
                  <div 
                    className="flex items-center gap-x-2 px-3 rounded-full text-sm font-medium"
                    style={{ backgroundColor: '#ef4444', color: 'white', height: '32px' }}
                  >
                    <span>Logged in as:</span>
                    <button
                      onClick={endImpersonation}
                      className="rounded-full p-0.5 transition-colors"
                      style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}
                      title="Exit Impersonation"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center gap-x-3 p-2 text-sm font-medium rounded-lg transition-all duration-200 text-secondary hover:text-primary hover:bg-hover"
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-r from-blue-500 to-indigo-600">
                    <span className="text-sm font-medium text-white">
                      {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                    </span>
                  </div>
                  <div className="hidden lg:block text-left">
                    <p className="text-sm font-medium text-primary">
                      {user?.role === 'admin' && !isImpersonating ? 'Younite Fitness' : `${user?.firstName} ${user?.lastName}`}
                    </p>
                  </div>
                  <ChevronDownIcon className="w-4 h-4 text-tertiary" />
                </button>

                {/* Profile Dropdown */}
                {profileDropdownOpen && (
                  <div className="dropdown-menu-profile">
                    {isImpersonating && (
                      <button
                        onClick={() => {
                          setProfileDropdownOpen(false);
                          endImpersonation();
                        }}
                        className="dropdown-item text-red-600 hover:bg-red-50 font-medium"
                      >
                        <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2" />
                        Exit Impersonation
                      </button>
                    )}
                    <Link
                      to="/profile"
                      onClick={() => setProfileDropdownOpen(false)}
                      className="dropdown-item"
                      >
                      {isAdmin && !isImpersonating ? (
                        <>
                          <CogIcon className="w-4 h-4 mr-2" />
                          Settings
                        </>
                      ) : (
                        <>
                          <UserIcon className="w-4 h-4 mr-2" />
                          Profile
                        </>
                      )}
                    </Link>
                    <button
                      onClick={() => {
                        setProfileDropdownOpen(false);
                        toggleDarkMode();
                      }}
                      className="dropdown-item"
                      >
                      {isDarkMode ? (
                        <>
                          <SunIcon className="w-4 h-4 mr-2" />
                          Light Mode
                        </>
                      ) : (
                        <>
                          <MoonIcon className="w-4 h-4 mr-2" />
                          Dark Mode
                        </>
                      )}
                    </button>
                    {!isFollowingSystem && (
                      <button
                        onClick={() => {
                          setProfileDropdownOpen(false);
                          resetToSystemPreference();
                        }}
                        className="dropdown-item"
                        >
                        <CogIcon className="w-4 h-4 mr-2" />
                        Follow System
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setProfileDropdownOpen(false);
                        handleLogout();
                      }}
                      className="dropdown-item"
                      >
                      <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="mx-auto px-4 sm:px-6 max-w-90rem">
            <Outlet />
          </div>
        </main>
      </div>

      <ConcessionTypesModal
        isOpen={showConcessionsModal}
        onClose={() => setShowConcessionsModal(false)}
      />
    </div>
  );
};

export default Layout;
